import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/legacy/image';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import Stories from 'react-insta-stories';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import { ISuggestion } from '../../common/model/suggestion.model';
import useI18n from '../../common/hooks/useI18n';

type Props = {
  query: string;
  suggestions: ISuggestion[];
  theme: DefaultTheme;
};

const Title = styled.h3`
  ${(props) => props.theme.font.h2}
  color: ${(props) => props.theme.colors.secondary};
`;

const Divider = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  border-top: 1px solid #e0e0e0;
`;

const StoriesContainer = styled.div`
  width: 100%;
  height: 540px;

  > div {
    background: transparent !important;
  }

  .story-container {
    position: relative;
    width: 100%;
    height: 100%;
    background: white;
    border-radius: ${(props) => props.theme.borderRadius};
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-radius: ${(props) => props.theme.borderRadius};
`;

const Gradient = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.25) 0%,
    rgba(0, 0, 0, 0) 90.77%
  );
  border-radius: ${(props) => props.theme.borderRadius};
  pointer-events: none;
`;

const StoryArrow = styled.div<{ $left: boolean }>`
  position: absolute;
  top: 50%;
  left: ${(props) => (props.$left ? '16px' : 'unset')};
  right: ${(props) => (props.$left ? 'unset' : '16px')};
  transform: translateY(calc(-50% - 24px));
  font-size: 24px;
  color: white;
  pointer-events: none;
  cursor: pointer;

  @media ${(props) => props.theme.breakpoints.mdd} {
    transform: translateY(calc(-50% - 18px));
    font-size: 18px;
  }
`;

const StoryContent = styled.div`
  position: absolute;
  padding: 24px;
  left: 0;
  bottom: 0;
  z-index: 1000;

  a {
    color: white !important;
  }
`;

const PlaceSuggestions = ({
  query,
  suggestions,
  theme,
}: Props): JSX.Element => {
  const i18n = useI18n();

  const titleKey = 'suggestionsTitle';

  return (
    <div>
      <Title>
        {i18n.t(`dynamic.homePage.${query}.${titleKey}`) ||
          i18n.t(`homePage.${titleKey}`)}
      </Title>
      <Divider />
      <StoriesContainer>
        <Stories
          stories={suggestions.map((s) => ({
            content: ({ action }) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                setTimeout(() => action('play'));
              }, []);
              return (
                <div className="story-container">
                  <ImageAbsoluteContainer>
                    <ImageContainer>
                      <Image
                        alt={s.title}
                        layout="fill"
                        objectFit="cover"
                        objectPosition={s.objectPosition}
                        src={i18n.stagingT(s.imageUrlKey)}
                        sizes={theme.getImageSizes(
                          '100vw',
                          '100vw',
                          '100vw',
                          '33vw'
                        )}
                      />
                    </ImageContainer>
                  </ImageAbsoluteContainer>
                  <Gradient />
                  <StoryArrow as="div" $left={true}>
                    <LeftOutlined />
                  </StoryArrow>
                  <StoryArrow as="div" $left={false}>
                    <RightOutlined />
                  </StoryArrow>
                  <StoryContent>
                    <Link
                      href={{
                        pathname: s.query,
                      }}
                      legacyBehavior
                    >
                      <a>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ fontSize: '24px', fontWeight: 500 }}>
                            {s.title}
                          </span>
                        </div>
                        <div>
                          <span>{i18n.t('homePage.suggestions.button')}</span>
                        </div>
                      </a>
                    </Link>
                  </StoryContent>
                </div>
              );
            },
          }))}
          defaultInterval={5000}
          width="inherit"
          height="inherit"
          loop
        />
      </StoriesContainer>
    </div>
  );
};

export default withTheme(PlaceSuggestions);
